import { useState } from 'react';
import { RefreshCw, AlertCircle, CheckCircle, Package, Users, Settings, LogOut } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { ItemEditor, AdminUsers } from '../components';

type TabType = 'items' | 'updates' | 'users' | 'settings';

export function Admin() {

  const [activeTab, setActiveTab] = useState<TabType>('items');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateResult, setUpdateResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const { user, isAdmin } = useAuth();

  const handleSignOut = () => {
    // Clear ALL localStorage immediately, synchronously
    localStorage.clear();
    // Hard redirect to login page — no async, no React, no waiting
    window.location.replace('/admin/login');
  };

  const triggerPriceUpdate = async () => {
    if (!isSupabaseConfigured) {
      setUpdateResult({
        success: false,
        message: 'Supabase is not configured. Please set up your environment variables.',
      });
      return;
    }

    setIsUpdating(true);
    setUpdateResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('update-prices');

      if (error) throw error;

      setUpdateResult({
        success: true,
        message: `Price update completed. Processed: ${data?.stats?.processed || 0}, Updated: ${data?.stats?.updated || 0}`,
      });
    } catch (error) {
      setUpdateResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to trigger price update',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const tabs = [
    { id: 'items' as TabType, label: 'Items', icon: Package },
    { id: 'updates' as TabType, label: 'Price Updates', icon: RefreshCw },
    { id: 'users' as TabType, label: 'Admin Users', icon: Users },
    { id: 'settings' as TabType, label: 'Settings', icon: Settings },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Admin Panel</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage items, prices, and admin users
          </p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {user?.email}
          </span>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Sign out clicked');
              handleSignOut();
            }}
            type="button"
            className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors flex items-center gap-2 cursor-pointer"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </div>

      {/* Non-admin notice */}
      {!isAdmin && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
          <p className="text-sm text-amber-800 dark:text-amber-100">
            You are signed in but do not have admin permissions. Please contact a system administrator
            if you believe this is an error.
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-dark-700">
        <nav className="flex gap-4 -mb-px">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-dark-600'
              }`}
              disabled={!isAdmin}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-700 p-6">
        {!isAdmin ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Admin tools are disabled for this account.
          </p>
        ) : (
          <>
        {/* Items Tab */}
        {activeTab === 'items' && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Item Management
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Add, edit, or delete items and their prices.
            </p>
            <ItemEditor />
          </div>
        )}

        {/* Price Updates Tab */}
        {activeTab === 'updates' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Price Update
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Trigger a manual price update for all items. This will fetch current prices from
                SerpApi and recalculate the price tiers.
              </p>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={triggerPriceUpdate}
                disabled={isUpdating}
                className="px-4 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center gap-2"
              >
                {isUpdating ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Updating prices...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Trigger Price Update
                  </>
                )}
              </button>

              <div className="flex items-center gap-2">
                <div
                  className={`w-2.5 h-2.5 rounded-full ${
                    isSupabaseConfigured ? 'bg-green-500' : 'bg-red-500'
                  }`}
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {isSupabaseConfigured ? 'Connected' : 'Not configured'}
                </span>
              </div>
            </div>

            {updateResult && (
              <div
                className={`p-4 rounded-lg flex items-start gap-3 ${
                  updateResult.success
                    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                }`}
              >
                {updateResult.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                )}
                <p className={updateResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}>
                  {updateResult.message}
                </p>
              </div>
            )}

            {/* API Info */}
            <div className="bg-gray-50 dark:bg-dark-700/50 rounded-lg p-4 mt-6">
              <h3 className="font-medium text-gray-900 dark:text-white mb-3">API Configuration</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">SERPAPI_KEY</span>
                  <span className="text-gray-500 dark:text-gray-500">Set in Supabase Edge Functions</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">EBAY_APP_ID</span>
                  <span className="text-gray-500 dark:text-gray-500">Optional fallback</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Admin Users Tab */}
        {activeTab === 'users' && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Admin User Management
            </h2>
            <AdminUsers />
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                System Settings
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                View system configuration and status.
              </p>
            </div>

            <div className="grid gap-4">
              <div className="bg-gray-50 dark:bg-dark-700/50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 dark:text-white mb-3">Connection Status</h3>
                <div className="flex items-center gap-3">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      isSupabaseConfigured ? 'bg-green-500' : 'bg-red-500'
                    }`}
                  />
                  <span className="text-gray-700 dark:text-gray-300">
                    {isSupabaseConfigured
                      ? 'Connected to Supabase'
                      : 'Supabase not configured'}
                  </span>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-dark-700/50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 dark:text-white mb-3">Database Schema</h3>
                <pre className="text-sm text-gray-600 dark:text-gray-400 overflow-x-auto">
{`Tables:
  - items: Household items with price tiers
  - categories: Item categories
  - price_history: Historical price data
  - update_logs: ETL job logs
  - item_prices_multi: Multi-currency prices
  - api_usage: API quota tracking`}
                </pre>
              </div>

              <div className="bg-gray-50 dark:bg-dark-700/50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 dark:text-white mb-3">Current User</h3>
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <p><strong>Email:</strong> {user?.email}</p>
                  <p><strong>ID:</strong> {user?.id}</p>
                  <p><strong>Last Sign In:</strong> {user?.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleString() : 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}
